/**
 * src/admin-ui/src/utils/usageTableFilters.ts
 *
 * Excel-style client-side column filtering for the model-usage table.
 *
 * A column filter combines two mechanisms that both must pass:
 * - a checkbox selection over the unique display values of the column
 *   (``selected === null`` means "all values allowed");
 * - a list of typed conditions (substring/equality for text columns,
 *   =/>/< for numeric columns) joined with AND or OR, as in the Excel
 *   custom autofilter.
 * Filters of different columns are always combined with AND.
 */

export type FilterConnective = "and" | "or";

export type TextOp = "contains" | "equals";

export type NumberOp = "eq" | "gt" | "lt";

export interface TextCondition {
  op: TextOp;
  value: string;
}

export interface NumberCondition {
  op: NumberOp;
  /** Raw user input; non-numeric input deactivates the condition. */
  value: string;
}

export interface ColumnFilterState {
  /** Checked display values; null = no checkbox restriction. */
  selected: string[] | null;
  textConditions: TextCondition[];
  numberConditions: NumberCondition[];
  connective: FilterConnective;
}

export type ColumnKind = "text" | "number";

export const emptyFilter = (): ColumnFilterState => ({
  selected: null,
  textConditions: [],
  numberConditions: [],
  connective: "and",
});

const activeTextConditions = (filter: ColumnFilterState): TextCondition[] =>
  filter.textConditions.filter((condition) => condition.value.trim() !== "");

const activeNumberConditions = (
  filter: ColumnFilterState,
): { op: NumberOp; value: number }[] =>
  filter.numberConditions
    .map((condition) => ({
      op: condition.op,
      value: Number(condition.value.replace(",", ".")),
    }))
    .filter((condition) => Number.isFinite(condition.value));

export const isFilterActive = (filter: ColumnFilterState): boolean =>
  filter.selected !== null ||
  activeTextConditions(filter).length > 0 ||
  activeNumberConditions(filter).length > 0;

const combine = (results: boolean[], connective: FilterConnective): boolean =>
  connective === "and" ? results.every(Boolean) : results.some(Boolean);

const matchesSelection = (
  displayValue: string,
  filter: ColumnFilterState,
): boolean =>
  filter.selected === null || filter.selected.includes(displayValue);

/**
 * Check a text-column cell against the filter.
 *
 * @param displayValue - Rendered cell text.
 * @param filter - Column filter state.
 * @returns Whether the row stays visible.
 */
export const matchesTextFilter = (
  displayValue: string,
  filter: ColumnFilterState,
): boolean => {
  if (!matchesSelection(displayValue, filter)) return false;
  const conditions = activeTextConditions(filter);
  if (conditions.length === 0) return true;
  const haystack = displayValue.toLowerCase();
  return combine(
    conditions.map((condition) => {
      const needle = condition.value.trim().toLowerCase();
      return condition.op === "equals"
        ? haystack === needle
        : haystack.includes(needle);
    }),
    filter.connective,
  );
};

/**
 * Check a numeric-column cell against the filter.
 *
 * @param numericValue - Cell value as a number.
 * @param displayValue - Rendered cell text (used by the checkbox set).
 * @param filter - Column filter state.
 * @returns Whether the row stays visible.
 */
export const matchesNumberFilter = (
  numericValue: number,
  displayValue: string,
  filter: ColumnFilterState,
): boolean => {
  if (!matchesSelection(displayValue, filter)) return false;
  const conditions = activeNumberConditions(filter);
  if (conditions.length === 0) return true;
  return combine(
    conditions.map((condition) => {
      switch (condition.op) {
        case "eq":
          return numericValue === condition.value;
        case "gt":
          return numericValue > condition.value;
        case "lt":
          return numericValue < condition.value;
        default:
          return true;
      }
    }),
    filter.connective,
  );
};

export interface FilterableCell {
  text: string;
  num?: number;
}

/**
 * Apply per-column filters to a list of rows.
 *
 * @param rows - Source rows.
 * @param filters - Filter state per column id.
 * @param getCell - Accessor returning the cell content for (row, columnId).
 * @param columnKinds - Kind per column id.
 * @returns Rows passing every active column filter.
 */
export function applyColumnFilters<Row>(
  rows: Row[],
  filters: Record<string, ColumnFilterState>,
  getCell: (row: Row, columnId: string) => FilterableCell,
  columnKinds: Record<string, ColumnKind>,
): Row[] {
  const activeEntries = Object.entries(filters).filter(([, filter]) =>
    isFilterActive(filter),
  );
  if (activeEntries.length === 0) return rows;
  return rows.filter((row) =>
    activeEntries.every(([columnId, filter]) => {
      const cell = getCell(row, columnId);
      return columnKinds[columnId] === "number"
        ? matchesNumberFilter(cell.num ?? 0, cell.text, filter)
        : matchesTextFilter(cell.text, filter);
    }),
  );
}

/**
 * Collect sorted unique display values of a column.
 *
 * @param rows - Source rows.
 * @param getCell - Accessor returning the cell content for (row, columnId).
 * @param columnId - Column id.
 * @returns Unique display values sorted for stable checkbox lists.
 */
export function uniqueColumnValues<Row>(
  rows: Row[],
  getCell: (row: Row, columnId: string) => FilterableCell,
  columnId: string,
): string[] {
  const seen = new Set<string>();
  for (const row of rows) {
    seen.add(getCell(row, columnId).text);
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, "ru"));
}
